<template>
  <i-dropdown
    @open="reset"
    :isOpen="item.showOptions"
    @update:isOpen="(o) => setFilterParamOptions({name: item.name, showOptions: o})"
  >
    <template v-slot:trigger="{toggle, attrs}">
      <i-chip
          @click="toggle"
          @close="() => disableFilterParam({name: item.name})"
          :filter="paramLabel"
          :noFilterLabel="LANG.all"
          v-bind="attrs">
          {{ item.label }}
      </i-chip>
    </template>

    <template v-slot:default="dropdown">
      <div class="dropdown-menu i-dropdown-menu block" style="margin-top: 2px;">
        <div class="pa2 pb1">
          <SearchText
            v-model="searchTerm"
            ref="searchInputRef"
            autocomplete="off"
            :placeholder="LANG.searchOptions"
            role="combobox"
            :aria-label="LANG.search"
            aria-controls="list-filters"
            aria-autocomplete="list"
            class="fts-as-you-type"
            />
        </div>
        <ul
          v-if="filteredOptions.length"
          role="presentation"
          tabindex="-1"
          aria-multiselectable="true"
          style="max-height: 350px; overflow-y: auto;">
            <li v-for="option in filteredOptions"
              :key="option.value"
              @click="()=>toggleOption(option)"
              @keydown.enter.stop.prevent="()=>toggleOption(option)"
              role="option"
              tabindex="0"
              class="ph2"
              :aria-label="option.display">
              <i class="fa5" :class="params.includes(option.value) ? 'fas fa-check-square': 'far fa-square gray'" name="check-square"></i>
              <span class="f5 pl1 va-middle">{{ option.display }}</span>
            </li>
            <div
              v-if="hasOptionsLimit && hiddenOptionsCount > 0"
              class="hidden-options-hint">
              {{LANG.hiddenOptionsWarning}}
            </div>
        </ul>
        <div
          class="options-hint"
          v-else>{{ LANG.noResultsMatched }} "{{ searchTerm }}"
        </div>

        <div class="button_container mv1 ph2">
          <button
            @click.prevent.stop="() => clear(dropdown)"
            @keydown.enter.stop="() => clear(dropdown)"
            :title="LANG.clear"
            class="btn i-button i-secondary-btn" >{{ LANG.clear }}</button>
          <button
            @click="dropdown.close"
            @keydown.enter.stop.prevent="dropdown.close"
            :title="LANG.close"
            class="btn i-button" >{{ LANG.close }}</button>
        </div>
      </div>
    </template>
  </i-dropdown>
</template>

<script>
import { mapState } from 'vuex';
import { mapBrowserMutations } from '@/store';
import SearchText from '@/components/common/SearchText.vue';
import { ascending } from '@/utils/tools';

export default {
  components: {
    SearchText,
  },
  props: {
    item: Object,
  },
  data() {
    return {
      searchTerm: '',
      options: [...this.item.options],
      hasOptionsLimit: !this.item.options_unlimited,
      optionsLimit: 10,
    };
  },
  methods: {
    ...mapBrowserMutations([
      'disableFilterParam',
      'setFilterParam',
      'setFilterParamOptions',
    ]),
    clear(dropdown) {
      this.params = [];
      dropdown.close();
    },
    reset() {
      this.resetSearch();
      this.resetOptions();
    },
    resetSearch() {
      this.searchTerm = '';

      const { searchInputRef } = this.$refs;
      if (searchInputRef) searchInputRef.focus();
    },
    resetOptions() {
      this.options = [...this.selectedOptions, ...this.unselectedOptions];
    },
    toggleOption(option) {
      if (this.params.includes(option.value)) {
        this.params = this.params.filter((p) => p !== option.value);
      } else {
        this.params = [...this.params, option.value];
      }
    },
  },
  computed: {
    ...mapState(['LANG']),
    params: {
      get() {
        return this.item.value;
      },
      set(params) {
        this.setFilterParam({
          name: this.item.name,
          value: params,
        });
      },
    },
    selectedOptions() {
      return this.options
        .filter((o) => this.params.includes(o.value))
        .sort((a, b) => ascending(a.display, b.display));
    },
    unselectedOptions() {
      return this.options
        .filter((o) => !this.params.includes(o.value))
        .sort((a, b) => ascending(a.display, b.display));
    },
    paramLabel() {
      return this.selectedOptions
        .map((o) => o.display)
        .join(', ');
    },
    filteredOptions() {
      return this.options
        .filter((o) => o.display.toLowerCase().includes(this.searchTerm.toLowerCase()))
        .slice(0, this.hasOptionsLimit ? this.optionsLimit : this.options.length);
    },
    hiddenOptionsCount() {
      return this.options.length - this.filteredOptions.length;
    },
  },
};
</script>

<style scoped lang="scss">
  .dropdown-menu {
    min-width: 250px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.18);
  }
  .dropdown-menu li {
    cursor: pointer;
    line-height: 25px;
  }
  .dropdown-menu li:hover {
    text-decoration: none;
    color: #262626;
    background-color: #f5f5f5;
  }
  .options-hint {
    padding: 10px;
    background: #f5f5f5;
    margin: 0 10px;
    overflow: hidden;
    max-width: 250px;
    white-space: nowrap;
  }
  .hidden-options-hint {
    background: $orange-light;
    color: #3D3D3D;
    font-style: italic;
    font-weight: 500;
    font-size: 14px;
    line-height: 17px;
    padding: 7px 15px;
    margin-top: 5px;
  }
  .fa5.gray {
    color: $gray;
  }
  .button_container .i-button {
    margin-left: 10px;
    margin-right: 0;
  }
</style>
